import React from "react";
import { Box, Container, Typography, Grid, Card, Chip } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import {
  FiberManualRecord, 
} from "@mui/icons-material";

import HeroSection from "../../components/HeroSection";
import Contact from "./Contact";
import rgreen from "../../images/robog.jpg";

// Feature icons
import sprayerIcon from "../../images/arm.png";
import payloadIcon from "../../images/payload.png";
import controlIcon from "../../images/connectivity.png";
import batteryIcon from "../../images/run.png";
import costIcon from "../../images/cost.png";
import electricIcon from "../../images/ev.png";

import ExploreIcon from "@mui/icons-material/Explore";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import NightlightIcon from "@mui/icons-material/Nightlight";
import SettingsIcon from "@mui/icons-material/Settings";
import SpeedIcon from "@mui/icons-material/Speed";
import StraightenIcon from "@mui/icons-material/Straighten";
import AdjustIcon from "@mui/icons-material/Adjust";

// Animations
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const ProductSection = styled(Box)(({ theme }) => ({
  position: "relative",
  padding: `${theme.spacing(8)} 0`,
  background: "linear-gradient(135deg, #000000 0%, #0a0a0a 50%, #000000 100%)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    width: "40%",
    height: "100%",
    background: "radial-gradient(circle, rgba(193, 227, 40, 0.1) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const ProductVideoContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: "400px",
  borderRadius: "20px",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: "linear-gradient(180deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.5) 100%)",
    zIndex: 1,
    pointerEvents: "none",
  },
}));

const ProductVideo = styled("video")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  filter: "brightness(0.8)",
});

const ProductCard = styled(Box)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.03)",
  backdropFilter: "blur(20px)",
  border: "1px solid rgba(193, 227, 40, 0.15)",
  borderRadius: "20px",
  padding: theme.spacing(4),
  position: "relative",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    border: "1px solid rgba(193, 227, 40, 0.3)",
    boxShadow: "0 20px 40px rgba(193, 227, 40, 0.15)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #c1e328 0%, #a8d020 100%)",
    borderRadius: "20px 20px 0 0",
  },
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  marginBottom: theme.spacing(2),
  "& .feature-icon": {
    width: "24px",
    height: "24px",
    borderRadius: "6px",
    background: "linear-gradient(135deg, #c1e328 0%, #a8d020 100%)",
    color: "#000000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(2),
    flexShrink: 0,
  },
  "& .feature-text": {
    color: "rgba(255, 255, 255, 0.9)",
    fontSize: "0.95rem",
    lineHeight: 1.5,
  },
}));


// Styled Components
const BackgroundBox = styled(Box)({
  position: "relative",
  minHeight: "100vh",
  background: "linear-gradient(180deg, #000000 0%, #0a0a0a 50%, #000000 100%)",
  overflow: "hidden",
  paddingTop: "80px",
  paddingBottom: "80px",
});

const GradientOrb = styled(Box)(({ top, left, bottom, right, color }) => ({
  position: "absolute",
  width: 500,
  height: 500,
  borderRadius: "50%",
  background: color,
  filter: "blur(100px)",
  pointerEvents: "none",
  top,
  left,
  bottom,
  right,
}));

const GridPattern = styled(Box)({
  position: "absolute",
  inset: 0,
  opacity: 0.05,
  backgroundImage: `linear-gradient(rgba(148, 163, 184, 0.1) 1px, transparent 1px),
                     linear-gradient(90deg, rgba(148, 163, 184, 0.1) 1px, transparent 1px)`,
  backgroundSize: "50px 50px",
  pointerEvents: "none",
});

const VideoContainer = styled(Box)({
  position: "relative",
  paddingBottom: "56.25%", // 16:9 aspect ratio
  height: 0,
  overflow: "hidden",
  borderRadius: 16,
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
  "& iframe": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    border: "none",
    borderRadius: 16,
  },
});

const FeatureCard = styled(Card)({
  background: "rgba(0, 0, 0, 0.7)",
  backdropFilter: "blur(20px)",
  borderRadius: 16,
  border: "1px solid rgba(193, 227, 40, 0.3)",
  padding: "24px",
  height: "100%",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    borderColor: "rgba(193, 227, 40, 0.5)",
    boxShadow: "0 10px 30px rgba(193, 227, 40, 0.2)",
  },
});

const FeatureIcon = styled(Box)(({ gradient }) => ({
  width: 48,
  height: 48,
  borderRadius: 12,
  background: "transparent",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
}));

const ProductImage = styled("img")({
  width: "100%",
  height: "auto",
  borderRadius: 16,
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
  transition: "transform 0.5s ease",
  "&:hover": {
    transform: "scale(1.02)",
  },
});

const features = [
  {
    image: sprayerIcon,
    title: "Robotic Sprayer Arm",
    description: "2 D.O.F High Pressure Sprayer System",
    gradient: "linear-gradient(135deg, #e4ff3a 0%, #c1e328 100%)",
  },
  {
    image: payloadIcon,
    title: "Heavy Payload",
    description: "500-1000Kg Payload, 3 Tons Towing Capacity",
    gradient: "linear-gradient(135deg, #c1e328 0%, #9fcc2e 100%)",
  },
  {
    image: controlIcon,
    title: "Easy Control",
    description: "Intuitive Remote Operation System",
    gradient: "linear-gradient(135deg, #c1e328 0%, #8ab82e 100%)",
  },
  {
    image: batteryIcon,
    title: "Long Runtime",
    description: "8-12 hours operation per charge",
    gradient: "linear-gradient(135deg, #b8d92e 0%, #9dc82e 100%)",
  },
  {
    image: costIcon,
    title: "Cost Effective",
    description: "50-70% reduction in input costs",
    gradient: "linear-gradient(135deg, #c1e328 0%, #b5d028 100%)",
  },
  {
    image: electricIcon,
    title: "Fully Electric",
    description: "Zero emissions, eco-friendly operation",
    gradient: "linear-gradient(135deg, #9fcc2e 0%, #8ab82e 100%)",
  },
];

function Home() {
  const offRoaderFeatures = [
    { icon: <SpeedIcon />, text: "500-1000 Kg Payload Capacity" },
    { icon: <StraightenIcon />, text: "3 Tons Towing Capacity" },
    { icon: <ExploreIcon />, text: "Full Day Runtime" },
    { icon: <AdjustIcon />, text: "Long Range Control" },
    { icon: <NightlightIcon />, text: "Night Vision" },
    { icon: <SettingsIcon />, text: "Customizable Control*" },
  ];

  const mistSprayerFeatures = [
    { icon: <WaterDropIcon />, text: "360° Coverage in Orchard Trees" },
    { icon: <SettingsIcon />, text: "2 D.O.F Dual Arm System" },
    { icon: <SpeedIcon />, text: "15-30Lpm with Mist size of 90-200 µm" },
    { icon: <StraightenIcon />, text: "7.5 Meters Spray Length from Nozzle" },
    { icon: <AdjustIcon />, text: "Width & Height Adjustable" },
  ];

  return (
    <>
      <HeroSection />

      {/* Robot as a Service Section */}
      <BackgroundBox>
        <GradientOrb top="20%" left={-256} color="rgba(193, 227, 40, 0.1)" />
        <GradientOrb
          bottom="20%"
          right={-256}
          color="rgba(193, 227, 40, 0.1)"
        />
        <GridPattern />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          {/* Section Header */}
          <Box textAlign="center" mb={6}>
            <Chip
              icon={
                <FiberManualRecord
                  sx={{
                    fontSize: 8,
                    animation: `${pulse} 2s ease-in-out infinite`,
                  }}
                />
              }
              label="INNOVATION"
              sx={{
                background: "rgba(0, 0, 0, 0.5)",
                backdropFilter: "blur(8px)",
                border: "1px solid rgba(193, 227, 40, 0.3)",
                color: "#c1e328",
                fontWeight: 700,
                letterSpacing: 3,
                fontSize: "0.625rem",
                mb: 3,
              }}
            />

            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                fontWeight: 700,
                color: "#fff",
                letterSpacing: -1,
                mb: 2,
              }}
            >
              Robot-as-a-
              <Box
                component="span"
                sx={{
                  background:
                    "linear-gradient(135deg, #c1e328 0%, #9fcc2e 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Service
              </Box>
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: "rgba(148, 163, 184, 1)",
                maxWidth: 800,
                mx: "auto",
                lineHeight: 1.7,
                fontSize: { xs: "1rem", md: "1.125rem" },
                mb: 6,
              }}
            >
              Let us take care of your spraying needs. Get more accurate and
              effective results at a fraction of the cost without any worries.
            </Typography>
          </Box>

          {/* Video Section */}
          <Box mb={8}>
            <VideoContainer>
              <iframe
                src="https://www.youtube.com/embed/33Swp0c7vMA?si=bF9OU4eB0ZJ1Bdrc"
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </VideoContainer>
          </Box>

          {/* Product Image with Value Propositions */}
          <Grid container spacing={6} alignItems="center" mt={8} mb={8}>
            <Grid item xs={12} md={6}>
              <ProductImage src={rgreen} alt="AUTOMIST500X Robot" />
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <Card
                    sx={{
                      background: "rgba(0, 0, 0, 0.5)",
                      backdropFilter: "blur(20px)",
                      borderRadius: 3,
                      border: "1px solid rgba(193, 227, 40, 0.3)",
                      p: 4,
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{ color: "#c1e328", fontWeight: 700, mb: 2 }}
                    >
                      Handle the 3D's
                    </Typography>
                    <Typography
                      sx={{ color: "rgba(203, 213, 225, 1)", lineHeight: 1.8 }}
                    >
                      Let us manage the <strong>Dull, Dirty, and Dangerous</strong>{" "}
                      environments while you focus on growing your business.
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Card
                    sx={{
                      background: "rgba(0, 0, 0, 0.5)",
                      backdropFilter: "blur(20px)",
                      borderRadius: 3,
                      border: "1px solid rgba(193, 227, 40, 0.3)",
                      p: 4,
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{ color: "#c1e328", fontWeight: 700, mb: 2 }}
                    >
                      ROI in 12 Months
                    </Typography>
                    <Typography
                      sx={{ color: "rgba(203, 213, 225, 1)", lineHeight: 1.8 }}
                    >
                      AUTOMIST500X costs a fraction of traditional mini tractor plus
                      sprayer. Get your return on investment in just one year.
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Features Section */}
          <Box textAlign="center" mb={6} mt={12}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "2rem", md: "2.5rem" },
                fontWeight: 700,
                color: "#fff",
                letterSpacing: -1,
                mb: 2,
              }}
            >
              Key{" "}
              <Box
                component="span"
                sx={{
                  background:
                    "linear-gradient(135deg, #c1e328 0%, #9fcc2e 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Features
              </Box>
            </Typography>
            <Typography
              sx={{
                color: "rgba(148, 163, 184, 1)",
                maxWidth: 600,
                mx: "auto",
                fontSize: "1rem",
              }}
            >
              Engineered for excellence with cutting-edge technology
            </Typography>
          </Box>

          <Grid container spacing={3} mb={6}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <FeatureCard elevation={0}>
                  {/* Icon and Title on same line */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <FeatureIcon gradient={feature.gradient}>
                      <img 
                        src={feature.image} 
                        alt={feature.title}
                        style={{ 
                          width: 28, 
                          height: 28, 
                          objectFit: "contain"
                        }} 
                      />
                    </FeatureIcon>
                    <Typography
                      variant="h6"
                      sx={{ 
                        color: "#fff", 
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        height: "100%"
                      }}
                    >
                      {feature.title}
                    </Typography>
                  </Box>
                  
                  {/* Description below with larger size */}
                  <Typography
                    variant="body1"
                    sx={{ 
                      color: "rgba(148, 163, 184, 1)", 
                      lineHeight: 1.6,
                      fontSize: "1rem"
                    }}
                  >
                    {feature.description}
                  </Typography>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
          <ProductSection>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            {/* Left - Video */}
            <Grid item xs={12} md={6}>
              <ProductVideoContainer>
                <ProductVideo
                  src="https://res.cloudinary.com/edajn7mf/video/upload/v1789857382/WhatsApp_Video_2026-09-20_at_4.05.49_AM.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              </ProductVideoContainer>
            </Grid>
            
            {/* Right - Details */}
            <Grid item xs={12} md={6}>
              <ProductCard>
                <Box sx={{ textAlign: "center", marginBottom: 4 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      marginBottom: 2,
                    }}
                  >
               
                    <Box
                      component="span"
                      sx={{
                        background:
                          "linear-gradient(135deg, #c1e328 0%, #b5d028 50%, #a8c728 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                     OFF-Roader
                    </Box>
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "rgba(255, 255, 255, 0.9)",
                      fontSize: "1.2rem",
                      marginBottom: 3,
                    }}
                  >
                    Built to explore the rough terrains
                  </Typography>
                </Box>

                <Box>
                  {offRoaderFeatures.map((feature, index) => (
                    <FeatureItem key={index}>
                      <Box className="feature-icon">
                        {React.cloneElement(feature.icon, {
                          sx: { fontSize: "1.1rem" },
                        })}
                      </Box>
                      <Typography className="feature-text">
                        {feature.text}
                      </Typography>
                    </FeatureItem>
                  ))}
                </Box>
              </ProductCard>
            </Grid>
          </Grid>
        </Container>
      </ProductSection>

      {/* Mist Sprayer ARMs Section */}
      <ProductSection>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            {/* Left - Details */}
            <Grid item xs={12} md={6}>
              <ProductCard>
                <Box sx={{ textAlign: "center", marginBottom: 4 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      marginBottom: 1,
                    }}
                  >
                   
                    <Box
                      component="span"
                      sx={{
                        background:
                          "linear-gradient(135deg, #c1e328 0%, #b5d028 50%, #a8c728 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                     2 D.O.F High Pressure
                    </Box>
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      marginBottom: 2,
                    }}
                  >
                    Mist Sprayer ARMs
                  </Typography>
                </Box>

                <Box>
                  {mistSprayerFeatures.map((feature, index) => (
                    <FeatureItem key={index}>
                      <Box className="feature-icon">
                        {React.cloneElement(feature.icon, {
                          sx: { fontSize: "1.1rem" },
                        })}
                      </Box>
                      <Typography className="feature-text">
                        {feature.text}
                      </Typography>
                    </FeatureItem>
                  ))}
                </Box>
              </ProductCard>
            </Grid>
            
            {/* Right - Video */}
            <Grid item xs={12} md={6}>
              <ProductVideoContainer>
                <ProductVideo
                  src="https://res.cloudinary.com/edajn7mf/video/upload/v1789996095/2.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              </ProductVideoContainer>
            </Grid>
          </Grid>
        </Container>
      </ProductSection>
        </Container>
      </BackgroundBox>

      <Contact />
    </>
  );
}

export default Home;
